import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sparkles, RefreshCw, AlertTriangle, Info, Trophy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAtlasAwardAnalysis, type AtlasBidFact } from "@/hooks/useAtlasAwardAnalysis";
import { displayCompanyName } from "@/lib/displayCompanyName";


/* ────────────────────────────────────────────────────────────────
 * Atlas Award Analysis is currently FREE (no credit / subscription gate).
 * We keep the reference-key + fingerprint plumbing so the gate can be
 * reintroduced later (planned: shipper subscription tier) without a
 * rewrite. The server-side cache in the atlas-award-analysis edge
 * function still prevents redundant AI calls per bid-set fingerprint.
 * ──────────────────────────────────────────────────────────────── */

/** Deterministic short hash of the bids fingerprint for the reference key. */
async function shortHash(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}


/* Chip metadata — every entry maps to a REAL field. */
type ChipMeta = { label: string; tone: "fact" | "claim" | "warn"; source: string; };

const CHIP_META: Record<string, ChipMeta> = {
  under_budget:            { label: "Under Budget",           tone: "fact",  source: "direct_bids.bid_amount < direct_loads.target_rate_max" },
  at_budget:               { label: "At Budget",              tone: "fact",  source: "direct_bids.bid_amount == direct_loads.target_rate_max" },
  over_budget:             { label: "Over Budget",            tone: "warn",  source: "direct_bids.bid_amount > direct_loads.target_rate_max" },
  meets_team_requirement:  { label: "Meets Team Requirement", tone: "fact",  source: "team_available bid on a load whose special_instructions require team" },
  missing_team_requirement:{ label: "Not Team-Capable",       tone: "warn",  source: "team_available=false on a load whose special_instructions require team" },
  truck_in_area:           { label: "Truck in Area (claim)",  tone: "claim", source: "direct_bids.truck_in_area — self-reported by broker" },
  ready_to_cover:          { label: "Ready to Cover (claim)", tone: "claim", source: "direct_bids.ready_to_cover — self-reported by broker" },
  team_available:          { label: "Team Available (claim)", tone: "claim", source: "direct_bids.team_available — self-reported by broker" },
  fastest_eta:             { label: "Fastest ETA (claim)",    tone: "claim", source: "lowest direct_bids.eta_hours among bids — self-reported" },
  meets_pickup_window:     { label: "Meets Pickup Window",    tone: "fact",  source: "claimed eta_hours + now <= direct_loads.pickup_date end-of-day" },
  verified_authority:      { label: "Verified Authority",     tone: "fact",  source: "companies.verification_status='verified' + documents_verified_at" },
  credit_score_strong:     { label: "Credit ≥ 700",           tone: "fact",  source: "companies.credit_score" },
  authority_established:   { label: "Authority 2+ yrs",       tone: "fact",  source: "companies.created_at age" },
  fast_response:           { label: "Fast Response",          tone: "fact",  source: "direct_bids.submitted_at within 30 min of direct_loads.created_at" },
};

function Chip({ chipKey }: { chipKey: string }) {
  const meta = CHIP_META[chipKey];
  if (!meta) return null;
  return (
    <span
      title={meta.source}
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium",
        meta.tone === "fact" && "bg-brand-teal/10 text-brand-teal border border-brand-teal/30",
        meta.tone === "claim" && "bg-muted text-muted-foreground border border-dashed border-border",
        meta.tone === "warn" && "bg-destructive/10 text-destructive border border-destructive/30",
      )}
    >
      {meta.label}
    </span>
  );
}

interface Props {
  loadId: string;
  bidsCount: number;
  bidsFingerprint: string;    // id:amount:status joined + sorted
  awarded: boolean;
}

export function AtlasAwardAnalysisPanel({ loadId, bidsCount, bidsFingerprint, awarded }: Props) {
  const enabled = !awarded && bidsCount >= 2;

  // Reference key for this exact bid set — retained for future subscription/
  // credit-gate reintroduction. Not used to charge today.
  const [refKey, setRefKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hash = await shortHash(bidsFingerprint || "empty");
      if (!cancelled) setRefKey(`atlas_award_${loadId}_${hash}`);
    })();
    return () => { cancelled = true; };
  }, [loadId, bidsFingerprint]);

  // Atlas is currently FREE. We keep refKey/idempotency plumbing above so
  // the subscription/credit gate can be reintroduced later without a rewrite.
  // Server-side cache in the edge function still prevents redundant AI calls.
  const unlocked = true;

  // AI query — server-side cache in the edge function returns cached results
  // for the same fingerprint with no AI call.
  const { data, isLoading, isError, error, refetch, isFetching } = useAtlasAwardAnalysis(
    loadId,
    { enabled: enabled && unlocked, bidsFingerprint },
  );

  const ranked = useMemo(() => {
    if (!data || "insufficient" in data) return [];
    const byId = new Map<string, AtlasBidFact>();
    data.bids.forEach((b) => byId.set(b.bid_id, b));
    return [...data.ranking]
      .sort((a, b) => a.rank - b.rank)
      .map((r) => ({ ...r, fact: byId.get(r.bid_id)! }))
      .filter((r) => r.fact);
  }, [data]);

  if (!enabled) return null;


  // ── UNLOCKED VIEW ──
  return (
    <div className="border-2 border-brand-teal/30 rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="bg-brand-teal/5 border-b border-brand-teal/20 p-3 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-bold text-sm text-foreground">Atlas Award Analysis</h3>
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
              Powered by Atlas AI — your freight decision-maker.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 shrink-0"
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          title="Refresh (cached — no charge, no AI call)"
        >
          <RefreshCw className={cn("h-3 w-3", (isLoading || isFetching) && "animate-spin")} />
        </Button>
      </div>

      {/* Body */}
      <div className="p-3 space-y-3">
        {isLoading && (
          <p className="text-xs text-muted-foreground italic">Atlas is analyzing your bids…</p>
        )}

        {isError && (
          <div className="flex items-start gap-2 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Atlas is unavailable right now.</p>
              <p className="text-muted-foreground mt-0.5">{(error as Error)?.message ?? "Try again in a moment."}</p>
            </div>
          </div>
        )}

        {data && !("insufficient" in data) && (
          <>
            {data.tradeoff?.text && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5">
                <div className="flex items-start gap-2">
                  <Info className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-foreground leading-snug">{data.tradeoff.text}</p>
                </div>
              </div>
            )}

            <ol className="space-y-2">
              {ranked.map((r) => (
                <li
                  key={r.bid_id}
                  className={cn(
                    "rounded-md border p-2.5",
                    r.rank === 1 ? "border-brand-teal/40 bg-brand-teal/5" : "border-border bg-background",
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        "inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold",
                        r.rank === 1 ? "bg-brand-teal text-white" : "bg-muted text-foreground",
                      )}
                    >
                      {r.rank === 1 ? <Trophy className="h-3 w-3" /> : r.rank}
                    </span>
                    <span className="text-xs font-semibold text-foreground truncate flex-1 min-w-0">
                      {displayCompanyName(r.fact.broker_company_name)}
                    </span>
                    <span className="text-xs font-bold tabular-nums text-foreground shrink-0">
                      ${r.fact.bid_amount.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug mb-1.5">
                    {r.rationale}
                  </p>
                  {r.fact.chips.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.fact.chips.map((c) => <Chip key={c} chipKey={c} />)}
                    </div>
                  )}
                </li>
              ))}
            </ol>

            <div className="flex items-center justify-between border-t border-border pt-2">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Atlas confidence
              </span>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] uppercase",
                  data.confidence === "medium" ? "border-brand-teal/40 text-brand-teal" : "border-muted-foreground/40 text-muted-foreground",
                )}
              >
                {data.confidence}
              </Badge>
            </div>

            <p className="text-[10px] text-muted-foreground italic text-center">
              Analysis based on live bid data and your load requirements.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
